import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Loader2, ShieldAlert } from 'lucide-react';
import api from '../../api';
import EmptyState from '../EmptyState';

/**
 * BD-08 — the Debriefs tab: this person's debriefs, and the calls they logged that have none yet.
 * Each row opens the meeting page at stage 3, where the debrief is read and edited.
 */

interface Counts {
  decisions: number;
  commitments: number;
  needs: number;
  risks: number;
  stakeholders: number;
  buying_signals: number;
  flagged: number;
  edited: number;
}
interface Row {
  meeting_id: number;
  title: string | null;
  start_at: string | null;
  status: 'running' | 'ready' | 'failed';
  source: string | null;
  generated_at: string | null;
  counts: Counts;
}
interface Waiting {
  meeting_id: number;
  title: string | null;
  start_at: string | null;
}

const SOURCE_LABEL: Record<string, string> = { transcript: 'Transcript', notes: 'Notes', voice: 'Voice' };

const when = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const zone = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(d).find((p) => p.type === 'timeZoneName')?.value || '';
  return `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} ${zone}`.trim();
};

const link = (id: number) => `/app/bd-meetings/${id}?stage=debrief`;

const DebriefsTab: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [waiting, setWaiting] = useState<Waiting[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    api.get('/api/bd/debriefs')
      .then((res) => {
        if (!active) return;
        setRows(res.data?.debriefs || []);
        setWaiting(res.data?.waiting || []);
        setFailed(false);
      })
      .catch(() => { if (active) setFailed(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-sm text-gray-400 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (failed) return <p className="text-sm text-red-600 py-8" data-testid="bd-debriefs-error">Your debriefs could not be loaded.</p>;
  if (!rows.length && !waiting.length) {
    return (
      <EmptyState
        icon={FileText}
        title="No debriefs yet"
        description="When you log a call (upload a transcript or paste notes), its debrief appears here: summary, decisions, commitments, needs and what was said about budget, timing and authority."
      />
    );
  }

  return (
    <div className="space-y-8" data-testid="bd-debriefs-tab">
      {waiting.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Logged, no debrief yet ({waiting.length})</h3>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg bg-white">
            {waiting.map((w) => (
              <li key={w.meeting_id}>
                <Link to={link(w.meeting_id)} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50" data-testid={`bd-debriefs-waiting-${w.meeting_id}`}>
                  <span className="text-sm text-gray-800">{w.title || 'Meeting'}</span>
                  <span className="text-xs text-gray-400">{when(w.start_at)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {rows.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Debriefs ({rows.length})</h3>
          <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg bg-white">
            {rows.map((r) => (
              <li key={r.meeting_id}>
                <Link to={link(r.meeting_id)} className="block px-4 py-3 hover:bg-gray-50" data-testid={`bd-debriefs-row-${r.meeting_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-gray-900">{r.title || 'Meeting'}</span>
                    <span className="text-xs text-gray-400">{when(r.start_at)}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {r.status === 'failed'
                      ? 'The last attempt did not finish'
                      : r.status === 'running'
                        ? 'Being made…'
                        : `${r.counts.decisions} decisions · ${r.counts.commitments} commitments · ${r.counts.needs} needs · ${r.counts.buying_signals} buying signals${r.counts.stakeholders ? ` · ${r.counts.stakeholders} new people` : ''}`}
                    {r.source && <span className="text-gray-400"> · {SOURCE_LABEL[r.source] || r.source}</span>}
                    {r.counts.edited > 0 && <span className="text-blue-600"> · edited</span>}
                    {r.counts.flagged > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-amber-700 ml-1" title="Some passages are flagged as sensitive and kept out of drafts and CRM notes.">
                        <ShieldAlert className="w-3 h-3" /> restricted
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default DebriefsTab;
